import { Trans, useLingui } from '@lingui/react/macro';
import { Plus, UserPlus } from 'lucide-react';

import { Pages } from 'modules/navigation/domain/enums/pages';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { useModalAddPlayer } from 'modules/teams/ui/hooks/use-modal-add-player';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AsyncState } from 'shared/utils/async-state';
import { theme } from 'styles/theme/theme';
import { UDEntityList } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import TeamPlayerRow from './team-player-row';

interface TeamsComponentsFormPlayersListPartProps {
  teamId: number;
  players: TeamPlayer[];
}

const TeamsComponentsFormPlayersListPart = (props: TeamsComponentsFormPlayersListPartProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { teamId, players } = props;

  const playerId = Number(useLocation().state?.fromPlayer);

  const [name, setName] = useState<string>('');
  const playersList = useMemo(() => {
    if (!teamId && !players.length) {
      return [];
    }

    // Поиск идёт по трём именам сразу и без разбора: запрос «петров» выдаёт и
    // Петрова, и Олега Петровича. Перенесено как есть.
    return players.filter((player) => {
      const query = name.toLowerCase();
      const firstName = player.firstName.toLowerCase();
      const surname = player.lastName.toLowerCase();
      const middleName = player.middleName ? player.middleName.toLowerCase() : '';

      return firstName.includes(query) || surname.includes(query) || middleName.includes(query);
    });
  }, [name, players, teamId]);

  const handleToCreateNewPlayer = useCallback(() => {
    navigate(Pages.TEAMS.builders.edit(teamId, 'players/create'), {
      state: playerId && { fromPlayer: playerId },
    });
  }, [navigate, playerId, teamId]);

  const { open: openModalAddPlayer, Modal: ModalAddPlayer } = useModalAddPlayer({ teamId });

  // Вкладка получает готовый массив и сети не видит: ветки загрузки и ошибки рисует
  // карточка команды выше по дереву (`team-edit-page.tsx`).
  const state: AsyncState = useMemo(
    () => ({ status: playersList.length > 0 ? 'ready' : 'empty', isRefreshing: false }),
    [playersList.length],
  );

  const addButtons = useMemo(
    () => (
      <>
        <UDButton className="flex items-center" variant="dark" onClick={openModalAddPlayer}>
          <UserPlus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
          <span>
            <Trans>Добавить игрока</Trans>
          </span>
        </UDButton>

        <UDButton className="flex items-center" variant="dark" onClick={handleToCreateNewPlayer}>
          <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
          <span>
            <Trans>Создать игрока</Trans>
          </span>
        </UDButton>
      </>
    ),
    [handleToCreateNewPlayer, openModalAddPlayer],
  );

  const renderPlayer = useCallback(
    (player: TeamPlayer, index: number) => <TeamPlayerRow teamId={teamId} player={player} index={index + 1} />,
    [teamId],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-6">
      <UDEntityList<TeamPlayer>
        items={playersList}
        renderItem={renderPlayer}
        getItemKey={(player) => player.id}
        state={state}
        search={name}
        onSearchChange={setName}
        searchPlaceholder={t`Поиск по игроку`}
        // Кнопки либо в тулбаре, либо под текстом пустого состояния, но не в обоих
        // местах сразу: на пустом составе они дублировали бы друг друга.
        actions={players.length > 0 ? addButtons : undefined}
        emptyText={t`В команде пока нет игроков`}
        emptySubtitle={t`Давайте добавим?`}
        emptyAction={<div className="flex flex-col gap-2">{addButtons}</div>}
        notFoundText={t`Игроки не найдены`}
      />

      {ModalAddPlayer}
    </div>
  );
};

export default TeamsComponentsFormPlayersListPart;
